import { create } from 'zustand'

import {
  deleteEpisodeHistory,
  deleteProductHistory,
  deleteReadHistory,
  getEpisodeProgress,
  getReadHistoryList,
  getRecentProducts,
  updateEpisodeProgress,
} from '@/api/api'
import { EpisodeProgress, ProductProgress, ReadHistory } from '@/api/type'

import { useMediaStore } from './useMediaStore'

/** 直近の進捗保存イベント（UIリアクティブ同期用） */
export type LastSavedProgress = {
  episodeId: number
  progress: number
  isCompleted: boolean
}

type ProgressState = {
  // 最近読んだプロダクト一覧
  recentProducts: ProductProgress[]
  // エピソード進捗のMap (episode_id → progress)
  episodeProgress: Map<number, EpisodeProgress>
  // 読書履歴一覧
  readHistory: ReadHistory[]
  // 直近の進捗保存イベント (UI同期用)
  lastSavedProgress: LastSavedProgress | null

  // ローディング状態
  isLoadingRecentProducts: boolean
  isLoadingHistory: boolean

  // アクション: プロダクト進捗
  fetchRecentProducts: (limit?: number) => Promise<void>

  // アクション: エピソード進捗
  fetchEpisodeProgress: (episodeId: number) => Promise<EpisodeProgress | null>
  /** API を呼ばずに進捗をストアへ反映する（自前で API を叩くプレイヤー／リーダー用） */
  applyEpisodeProgress: (episodeId: number, progress: number, isCompleted?: boolean) => void
  saveEpisodeProgress: (
    episodeId: number,
    progress: number,
    isCompleted?: boolean,
    incrementPlayCount?: boolean,
  ) => Promise<void>

  // アクション: 履歴取得・削除
  fetchReadHistory: (limit?: number, date?: string) => Promise<void>
  removeReadHistory: (historyId: number) => Promise<void>
  removeEpisodeHistory: (episodeId: number) => Promise<void>
  removeProductHistory: (productId: number) => Promise<void>
}

export const useProgressStore = create<ProgressState>((set, get) => ({
  recentProducts: [],
  episodeProgress: new Map(),
  readHistory: [],
  lastSavedProgress: null,
  isLoadingRecentProducts: false,
  isLoadingHistory: false,

  // ============================================================
  // プロダクト進捗
  // ============================================================

  fetchRecentProducts: async (limit = 20) => {
    set({ isLoadingRecentProducts: true })
    try {
      const response = await getRecentProducts(limit)
      set({ recentProducts: response.items })
    } catch (error) {
      console.error('Failed to fetch recent products:', error)
    } finally {
      set({ isLoadingRecentProducts: false })
    }
  },

  // ============================================================
  // エピソード進捗
  // ============================================================

  fetchEpisodeProgress: async (episodeId: number) => {
    try {
      const progress = await getEpisodeProgress(episodeId)
      if (progress) {
        set((state) => {
          const newMap = new Map(state.episodeProgress)
          newMap.set(episodeId, progress)
          return { episodeProgress: newMap }
        })
      }
      return progress
    } catch (error) {
      console.error('Failed to fetch episode progress:', error)
      return null
    }
  },

  applyEpisodeProgress: (episodeId: number, progress: number, isCompleted = false) => {
    set((state) => {
      const newMap = new Map(state.episodeProgress)
      newMap.set(episodeId, {
        episode_id: episodeId,
        cfi: null,
        locator: null,
        play_count: 0,
        ...newMap.get(episodeId),
        progress,
        is_completed: isCompleted,
        updated_at: new Date().toISOString(),
      })
      // lastSavedProgress は毎回新しい参照にして購読側の useEffect を発火させる
      return { episodeProgress: newMap, lastSavedProgress: { episodeId, progress, isCompleted } }
    })

    // ProductContainer のプロダクト一覧も同期更新
    useMediaStore.getState().updateEpisodeProgress(episodeId, progress, isCompleted)
  },

  saveEpisodeProgress: async (episodeId: number, progress: number, isCompleted = false, incrementPlayCount = false) => {
    // 楽観的更新: API 完了を待たずにストアを反映する
    get().applyEpisodeProgress(episodeId, progress, isCompleted)
    try {
      const response = await updateEpisodeProgress(episodeId, {
        progress,
        is_completed: isCompleted,
        ...(incrementPlayCount && { increment_play_count: true }),
      })

      set((state) => {
        const newMap = new Map(state.episodeProgress)
        newMap.set(episodeId, response)
        return { episodeProgress: newMap }
      })
    } catch (error) {
      console.error('Failed to save episode progress:', error)
    }
  },

  // ============================================================
  // 履歴取得・削除
  // ============================================================

  fetchReadHistory: async (limit = 100, date?: string) => {
    set({ isLoadingHistory: true })
    try {
      const response = await getReadHistoryList(limit, date)
      set({ readHistory: response.items })
    } catch (error) {
      console.error('Failed to fetch read history:', error)
    } finally {
      set({ isLoadingHistory: false })
    }
  },

  removeReadHistory: async (historyId: number) => {
    try {
      await deleteReadHistory(historyId)
      // 楽観的更新
      set((state) => ({
        readHistory: state.readHistory.filter((h) => h.id !== historyId),
      }))
    } catch (error) {
      console.error('Failed to delete read history:', error)
    }
  },

  removeEpisodeHistory: async (episodeId: number) => {
    try {
      await deleteEpisodeHistory(episodeId)
      // 楽観的更新
      set((state) => ({
        readHistory: state.readHistory.filter((h) => h.episode_id !== episodeId),
      }))
    } catch (error) {
      console.error('Failed to delete episode history:', error)
    }
  },

  removeProductHistory: async (productId: number) => {
    try {
      await deleteProductHistory(productId)
      // 履歴を再取得（プロダクトIDでのフィルタリングは複雑なため）
      await get().fetchReadHistory()
    } catch (error) {
      console.error('Failed to delete product history:', error)
    }
  },
}))
